import React, { useEffect, useMemo, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { ReportDataService, ReportListItem } from '../../services/reportDataService';
import { formatDate } from '../../services/sharedService';
import { formatDisplayDate, toDateString } from '../../services/dateUtils';
import PdfViewer from '../common/PdfViewer';
import { staleTier, stalePillLabel, staleTooltip, staleDetails } from '../../models/reportStaleness';
import './reportHistory.scss';
import HelpAsk from '../../help/HelpAsk';
import SupportRef, { reportRef } from '../SupportRef';

interface ReportHistoryProps {
  childProfiles: ChildProfile[];
  sidebarChildId: string | null;
  onRegenerateSuccess?: (message: string) => void;
}

type SortKey = 'period' | 'child' | 'generated';
type SortDir = 'asc' | 'desc';

const monthsAgo = (n: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return toDateString(d);
};

const today = (): string => toDateString(new Date());

const ReportHistory: React.FC<ReportHistoryProps> = ({ childProfiles, sidebarChildId, onRegenerateSuccess }) => {
  // Default range: last 3 months → today
  const [fromDate, setFromDate] = useState<string>(monthsAgo(3));
  const [toDate, setToDate] = useState<string>(today());

  // When the sidebar has a child selected, default to that child. The user
  // can switch to "All children" via the toggle.
  const [allChildren, setAllChildren] = useState<boolean>(!sidebarChildId);

  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('period');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [busyRows, setBusyRows] = useState<Set<number>>(new Set());

  // The row open in the inline viewer, same as the Documents grid. The URL is
  // minted on open (presigned URLs are short-lived) by the viewer's own loader.
  const [viewRow, setViewRow] = useState<ReportListItem | null>(null);

  // childId → ChildProfile (fast lookup for table rendering)
  const childMap = useMemo(() => {
    const m = new Map<number, ChildProfile>();
    childProfiles.forEach(c => m.set(c.id, c));
    return m;
  }, [childProfiles]);

  const childIdToQuery: number | null = allChildren
    ? null
    : sidebarChildId ? parseInt(sidebarChildId, 10) : null;

  // Sidebar selection changed and we're currently in "All children" mode →
  // automatically switch back to single-child if the user picks one.
  useEffect(() => {
    if (sidebarChildId) setAllChildren(false);
  }, [sidebarChildId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await ReportDataService.listReports({
        startDate: fromDate,
        endDate: toDate,
        childId: childIdToQuery,
      });
      setReports(rows);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fromDate, toDate, allChildren, sidebarChildId]);

  // Stable sort that pivots on the chosen key, with a sensible tiebreaker
  // (newest generated wins ties on period or child name).
  const sortedReports = useMemo(() => {
    const copy = [...reports];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'period') {
        cmp = a.endDate.localeCompare(b.endDate);
        if (cmp === 0) cmp = a.startDate.localeCompare(b.startDate);
      } else if (sortKey === 'child') {
        const an = childMap.get(a.childId);
        const bn = childMap.get(b.childId);
        const al = an ? `${an.first_name} ${an.last_name}` : `${a.childId}`;
        const bl = bn ? `${bn.first_name} ${bn.last_name}` : `${b.childId}`;
        cmp = al.localeCompare(bl);
      } else {
        cmp = a.createdAt.localeCompare(b.createdAt);
      }
      if (cmp === 0) cmp = a.createdAt.localeCompare(b.createdAt);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [reports, sortKey, sortDir, childMap]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'child' ? 'asc' : 'desc');
    }
  };

  const handleDownload = async (row: ReportListItem) => {
    setBusyRows(prev => { const next = new Set(prev); next.add(row.id); return next; });
    try {
      const dl = await ReportDataService.getReportDownload(row.id);
      // Open in a new tab — same pattern as GenerateReportPanel. Cross-origin
      // presigned S3 URLs ignore `download` on same-tab navigation.
      window.open(dl.url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to download report');
    } finally {
      setBusyRows(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  const handleRegenerate = async (row: ReportListItem) => {
    setBusyRows(prev => { const next = new Set(prev); next.add(row.id); return next; });
    try {
      // Preserve password if the original report had one — the user's
      // expectation is that regenerate produces the same kind of report,
      // just with newer content. We have to fetch the existing password
      // from the download endpoint because we don't expose it in the list.
      let password: string | undefined;
      if (row.isPasswordProtected) {
        const dl = await ReportDataService.getReportDownload(row.id);
        password = dl.password;
      }

      // Regenerate = same kind of report, newer content and current format —
      // so the row's template identity travels with it. null template_id
      // means the stock template (or a template deleted since generation;
      // the API then regenerates with the stock default and tells us).
      const result = await ReportDataService.generateReport({
        childId: row.childId,
        startDate: row.startDate,
        endDate: row.endDate,
        reportDate: row.reportDate,
        showHeader: true,
        pdfPassword: password,
        templateId: row.templateId ?? 'stock',
      });

      onRegenerateSuccess?.(result.templateMissing
        ? 'Report regenerated with the default Weekly Report — its original template no longer exists.'
        : 'Report regenerated successfully.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to regenerate report');
    } finally {
      setBusyRows(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  const periodLabel = (row: ReportListItem) => {
    const s = formatDisplayDate(row.startDate);
    const e = formatDisplayDate(row.endDate);
    return `${s} → ${e}`;
  };

  const childLabel = (row: ReportListItem): string => {
    const c = childMap.get(row.childId);
    return c ? `${c.first_name} ${c.last_name}` : 'Unknown child';
  };

  const childCell = (row: ReportListItem) => {
    const c = childMap.get(row.childId);
    if (!c) return <span className="rh-child rh-child--unknown">Unknown child</span>;
    return (
      <span className="rh-child">
        <span className="rh-child-swatch" style={{ background: c.colour_code || '#888' }}>
          {c.initials}
        </span>
        <span className="rh-child-name">{c.first_name} {c.last_name}</span>
      </span>
    );
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return <i className="fas fa-sort rh-sort-icon rh-sort-icon--idle" />;
    return sortDir === 'asc'
      ? <i className="fas fa-sort-up rh-sort-icon" />
      : <i className="fas fa-sort-down rh-sort-icon" />;
  };

  return (
    <div className="report-history">
      <div className="pill-toolbar" role="toolbar" aria-label="Report history filters" data-help="history.filters">
        <label className="rh-field">
          <span className="rh-field__label">From</span>
          <input
            type="date"
            className="rh-date-input"
            value={fromDate}
            max={toDate}
            onChange={e => setFromDate(e.target.value)}
          />
        </label>
        <label className="rh-field">
          <span className="rh-field__label">To</span>
          <input
            type="date"
            className="rh-date-input"
            value={toDate}
            min={fromDate}
            onChange={e => setToDate(e.target.value)}
          />
        </label>
        <div className="rh-shortcut-group" role="group" aria-label="Quick ranges">
          <button type="button" className="rh-shortcut" onClick={() => { setFromDate(monthsAgo(1)); setToDate(today()); }}>1m</button>
          <button type="button" className="rh-shortcut" onClick={() => { setFromDate(monthsAgo(3)); setToDate(today()); }}>3m</button>
          <button type="button" className="rh-shortcut" onClick={() => { setFromDate(monthsAgo(6)); setToDate(today()); }}>6m</button>
          <button type="button" className="rh-shortcut" onClick={() => { setFromDate(monthsAgo(12)); setToDate(today()); }}>1y</button>
        </div>

        <div className="pill-toolbar__spacer" />

        <label className={`rh-toggle ${!sidebarChildId ? 'rh-toggle--locked' : ''}`}>
          <input
            type="checkbox"
            checked={allChildren}
            disabled={!sidebarChildId}
            onChange={e => setAllChildren(e.target.checked)}
          />
          <span className="rh-toggle__track">
            <span className="rh-toggle__thumb" />
          </span>
          <span className="rh-toggle__label">
            All children
          </span>
        </label>
        <button
          type="button"
          className="pill-toolbar__icon-btn"
          onClick={() => void load()}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh"
        >
          <i className={`fas fa-sync-alt ${loading ? 'rh-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="rh-error">
          <i className="fas fa-exclamation-circle" />
          <span>{error}</span>
          <button type="button" className="rh-error__dismiss" onClick={() => setError(null)} aria-label="Dismiss">
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      <div className="rh-table-wrap">
        <table className="rh-table">
          <thead>
            <tr>
              <th className="rh-th rh-th--sortable" onClick={() => toggleSort('child')}>
                <span>Child</span>{sortIndicator('child')}
              </th>
              <th className="rh-th rh-th--sortable" onClick={() => toggleSort('period')}>
                <span>Period</span>{sortIndicator('period')}
              </th>
              <th className="rh-th">Template</th>
              <th className="rh-th rh-th--sortable" onClick={() => toggleSort('generated')}>
                <span>Generated</span>{sortIndicator('generated')}
              </th>
              <th className="rh-th rh-th--center">Status</th>
              <th className="rh-th rh-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && reports.length === 0 && (
              <tr>
                <td colSpan={6} className="rh-empty">
                  <div className="loading-spinner rh-spinner" />
                  <span>Loading reports…</span>
                </td>
              </tr>
            )}
            {!loading && sortedReports.length === 0 && (
              <tr>
                <td colSpan={6} className="rh-empty">
                  <i className="fas fa-folder-open rh-empty__icon" />
                  <span>No reports in this date range.</span>
                  <HelpAsk guideKey="reports.generate-pdf" label="How do I generate a report?" variant="inline" />
                </td>
              </tr>
            )}
            {sortedReports.map((row, rowIndex) => {
              const busy = busyRows.has(row.id);
              // Only content staleness colours the row and accents Regenerate.
              // A branding change touches every report in the org at once, so
              // tinting rows for it would light up the whole table.
              const tier = staleTier(row.staleReasons);
              const needsAttention = tier === 'content';
              return (
                <tr key={row.id} className={needsAttention ? 'rh-row rh-row--stale' : 'rh-row'}>
                  <td className="rh-td">{childCell(row)}</td>
                  <td className="rh-td rh-td--period">
                    {/* Clicking the period opens the report inline — the
                        Documents grid opens on its name cell the same way. */}
                    <button type="button" className="rh-open" onClick={() => setViewRow(row)} title="View report">
                      <i className="fas fa-file-pdf" />
                      <span>{periodLabel(row)}</span>
                    </button>
                  </td>
                  <td className="rh-td rh-td--template">{row.templateName}</td>
                  <td className="rh-td rh-td--generated">
                    <span className="rh-generated-by">{row.createdByName}</span>
                    <span className="rh-generated-at">{formatDate(row.createdAt)}</span>
                    {/* Anchored on the first row only: the ring spans every
                        element carrying an anchor, and one Ref shows where
                        they all are. */}
                    <SupportRef
                      code={reportRef(row.id)}
                      className="rh-ref"
                      dataHelp={rowIndex === 0 ? 'history.support-ref' : undefined}
                    />
                  </td>
                  <td className="rh-td rh-td--center">
                    {/* The pill names the leading reason (or counts them) and
                        spells all of them out on hover. */}
                    {tier ? (
                      <span
                        className={`rh-badge rh-badge--${needsAttention ? 'stale' : 'cosmetic'}`}
                        title={staleTooltip(row.staleReasons)}
                      >
                        <i className={`fas ${needsAttention ? 'fa-exclamation-triangle' : 'fa-palette'}`} />
                        {stalePillLabel(row.staleReasons)}
                      </span>
                    ) : (
                      <span className="rh-badge rh-badge--current" title={staleTooltip(row.staleReasons)}>
                        <i className="fas fa-check-circle" /> Current
                      </span>
                    )}
                    {row.isPasswordProtected && (
                      <span className="rh-lock" title="Password-protected PDF">
                        <i className="fas fa-lock" />
                      </span>
                    )}
                  </td>
                  <td className="rh-td rh-td--actions">
                    <button
                      type="button"
                      className="rh-action rh-action--view"
                      onClick={() => setViewRow(row)}
                      title="View report"
                    >
                      <i className="fas fa-eye" /> <span>View</span>
                    </button>
                    <button
                      type="button"
                      className="rh-action rh-action--download"
                      data-help="history.download"
                      onClick={() => void handleDownload(row)}
                      disabled={busy}
                      title="Download PDF"
                    >
                      {busy ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-arrow-down" /> <span>Download</span></>}
                    </button>
                    <button
                      type="button"
                      className={`rh-action rh-action--regenerate ${needsAttention ? 'rh-action--accent' : ''}`}
                      onClick={() => void handleRegenerate(row)}
                      disabled={busy}
                      title={tier ? `Regenerate — ${staleDetails(row.staleReasons)[0]}` : 'Regenerate report'}
                    >
                      <i className="fas fa-sync-alt" />
                      <span>Regenerate</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {viewRow && (
        <PdfViewer
          sourceKey={viewRow.id}
          title={`${childLabel(viewRow)} — ${periodLabel(viewRow)}`}
          load={async () => {
            const dl = await ReportDataService.getReportDownload(viewRow.id);
            return { url: dl.url, password: dl.password };
          }}
          missingHint="Regenerate this report to create a new PDF."
          onClose={() => setViewRow(null)}
        />
      )}

      <div className="rh-footer">
        <span className="rh-count">
          {loading
            ? 'Loading…'
            : `${sortedReports.length} report${sortedReports.length === 1 ? '' : 's'}`
          }
        </span>
        <span className="rh-range-label">
          {formatDisplayDate(fromDate)} → {formatDisplayDate(toDate)}
        </span>
      </div>
    </div>
  );
};

export default ReportHistory;
